import type { InputHTMLAttributes, ReactElement } from 'react'
import { useEffect, useId, useRef } from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  indeterminate?: boolean
}

export function Checkbox({
  label,
  indeterminate = false,
  className,
  id,
  disabled,
  ...rest
}: CheckboxProps): ReactElement {
  const generatedId = useId()
  const inputId = id ?? `checkbox-${generatedId}`
  const inputRef = useRef<HTMLInputElement>(null)

  // `indeterminate` exists only as a DOM property, never as an attribute.
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate
  }, [indeterminate])

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'group inline-flex items-start gap-2.5',
        disabled === true ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
        className
      )}
    >
      <span className="relative mt-px grid size-[1.125rem] shrink-0 place-items-center">
        <input
          {...rest}
          ref={inputRef}
          id={inputId}
          type="checkbox"
          disabled={disabled}
          className={cn(
            'peer size-full cursor-[inherit] appearance-none rounded-[0.3125rem] border bg-surface transition-colors dark:bg-sunken',
            'border-line-strong hover:border-accent/70',
            'checked:border-accent checked:bg-accent indeterminate:border-accent indeterminate:bg-accent',
            'disabled:border-line disabled:bg-sunken'
          )}
        />
        {indeterminate ? (
          <Minus
            strokeWidth={3.25}
            className="pointer-events-none absolute size-3 text-accent-fg opacity-0 peer-indeterminate:opacity-100"
            aria-hidden="true"
          />
        ) : (
          <Check
            strokeWidth={3.25}
            className="pointer-events-none absolute size-3 text-accent-fg opacity-0 peer-checked:opacity-100"
            aria-hidden="true"
          />
        )}
      </span>
      {label ? <span className="select-none text-sm leading-[1.15rem] text-fg">{label}</span> : null}
    </label>
  )
}
